import React, { useState, useEffect } from 'react';
import axiosInstance from '@/lib/axios';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, Hammer, TrendingUp, Calendar, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip as ChartTooltip } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#00F0FF', '#7000FF', '#00FF9F', '#FFD600', '#FF003C', '#FF6B9D'];

export default function PropertyDetail({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [categories, setCategories] = useState({ travaux: [], amenagement: [] });
  
  // Expense form
  const [expenseForm, setExpenseForm] = useState({
    category: 'travaux',
    subcategory: '',
    description: '',
    amount: '',
    date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    loadProperty();
    loadCategories();
  }, [id]);

  const loadProperty = async () => {
    try {
      const response = await axiosInstance.get(`${API}/properties/${id}`, {
        withCredentials: true
      });
      setProperty(response.data);
    } catch (error) {
      console.error('Error loading property:', error);
      toast.error('Bien introuvable');
      navigate('/properties');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await axiosInstance.get(`${API}/expense-categories`, {
        withCredentials: true
      });
      setCategories(response.data);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const handleAddExpense = async () => {
    if (!expenseForm.category || !expenseForm.subcategory || !expenseForm.amount) {
      toast.error('Remplis tous les champs obligatoires !');
      return;
    }

    try {
      await axiosInstance.post(`${API}/expenses`, {
        property_id: id,
        category: expenseForm.category,
        subcategory: expenseForm.subcategory,
        description: expenseForm.description,
        amount: parseFloat(expenseForm.amount),
        date: expenseForm.date
      }, {
        withCredentials: true
      });

      toast.success('💰 Dépense enregistrée ! Budget mis à jour 📊');
      setShowAddExpense(false);
      setExpenseForm({
        category: 'travaux',
        subcategory: '',
        description: '',
        amount: '',
        date: new Date().toISOString().split('T')[0]
      });
      loadProperty();
    } catch (error) {
      console.error('Error adding expense:', error);
      toast.error('Erreur lors de l\'ajout de la dépense');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030305] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-cyan-500 border-t-transparent"></div>
          <p className="mt-4 text-cyan-400 font-['Rajdhani']">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!property) return null;

  // Prepare chart data
  const expensesBySubcategory = {};
  property.expenses?.forEach(exp => {
    if (!expensesBySubcategory[exp.subcategory]) {
      expensesBySubcategory[exp.subcategory] = 0;
    }
    expensesBySubcategory[exp.subcategory] += exp.amount;
  });

  const chartData = Object.entries(expensesBySubcategory).map(([name, value]) => ({
    name,
    value
  }));

  return (
    <div className="min-h-screen bg-[#030305] relative">
      <div className="cyber-grid-bg"></div>

      <div className="relative z-10">
        {/* Header */}
        <header className="border-b border-white/10 backdrop-blur-xl bg-black/20">
          <div className="container mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <Button
                onClick={() => navigate('/properties')}
                data-testid="back-button"
                variant="ghost"
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Mes biens
              </Button>
              <h1 className="text-xl font-bold font-['Orbitron'] tracking-wider text-white">
                {property.name}
              </h1>
              <div className="w-32"></div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="container mx-auto px-6 py-12 max-w-7xl">
          {/* Property Info Card */}
          <div className="glass-panel p-8 rounded-lg mb-8">
            <div className="grid md:grid-cols-3 gap-8">
              {/* Photos */}
              <div className="md:col-span-1">
                {property.photo_urls && property.photo_urls.length > 0 ? (
                  <div className="space-y-3">
                    <img
                      src={property.photo_urls[0]}
                      alt={property.name}
                      className="w-full h-48 object-cover rounded-lg border border-cyan-500/30"
                    />
                    {property.photo_urls.length > 1 && (
                      <div className="grid grid-cols-3 gap-2">
                        {property.photo_urls.slice(1, 4).map((url, idx) => (
                          <img
                            key={idx}
                            src={url}
                            alt={`Photo ${idx + 2}`}
                            className="w-full h-20 object-cover rounded border border-cyan-500/20"
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="w-full h-48 bg-gradient-to-br from-cyan-900/20 to-purple-900/20 rounded-lg flex items-center justify-center border border-cyan-500/20">
                    <span className="text-6xl">🏠</span>
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="md:col-span-2">
                <h2 className="text-3xl font-bold font-['Orbitron'] text-white mb-4">
                  {property.name}
                </h2>
                <p className="text-slate-400 mb-6">📍 {property.address}</p>

                {property.description && (
                  <p className="text-slate-300 mb-6">{property.description}</p>
                )}

                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 bg-cyan-500/5 border border-cyan-500/20 rounded-lg">
                    <div className="text-sm text-slate-400 mb-1">Surface</div>
                    <div className="text-2xl font-bold text-white">{property.surface_m2} m²</div>
                  </div>
                  <div className="p-4 bg-cyan-500/5 border border-cyan-500/20 rounded-lg">
                    <div className="text-sm text-slate-400 mb-1">Pièces</div>
                    <div className="text-2xl font-bold text-white">{property.nb_rooms}P</div>
                  </div>
                  <div className="p-4 bg-cyan-500/5 border border-cyan-500/20 rounded-lg">
                    <div className="text-sm text-slate-400 mb-1">Code postal</div>
                    <div className="text-2xl font-bold text-white">{property.postal_code}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs: Dépenses & Simulations */}
          <Tabs defaultValue="expenses" className="w-full">
            <TabsList className="grid w-full max-w-md grid-cols-2 bg-black/50 border border-white/10">
              <TabsTrigger value="expenses" className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
                💰 Dépenses
              </TabsTrigger>
              <TabsTrigger value="simulations" className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
                📊 Simulations
              </TabsTrigger>
            </TabsList>

            {/* Expenses Tab */}
            <TabsContent value="expenses" className="mt-6">
              {/* Summary Cards */}
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="glass-panel p-6 rounded-lg">
                  <div className="flex items-center gap-3 mb-3">
                    <Hammer className="h-6 w-6 text-cyan-400" />
                    <h3 className="text-lg font-semibold text-white">Travaux</h3>
                  </div>
                  <div className="text-3xl font-bold text-cyan-400">
                    {property.total_travaux?.toLocaleString() || 0} €
                  </div>
                </div>

                <div className="glass-panel p-6 rounded-lg">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-2xl">🛋️</span>
                    <h3 className="text-lg font-semibold text-white">Aménagement</h3>
                  </div>
                  <div className="text-3xl font-bold text-purple-400">
                    {property.total_amenagement?.toLocaleString() || 0} €
                  </div>
                </div>

                <div className="glass-panel p-6 rounded-lg border-2 border-cyan-500/30">
                  <div className="flex items-center gap-3 mb-3">
                    <TrendingUp className="h-6 w-6 text-cyan-400" />
                    <h3 className="text-lg font-semibold text-white">Total Investi</h3>
                  </div>
                  <div className="text-3xl font-bold text-white">
                    {((property.total_travaux || 0) + (property.total_amenagement || 0)).toLocaleString()} €
                  </div>
                </div>
              </div>

              {/* Add Expense Button */}
              <div className="mb-6">
                <Dialog open={showAddExpense} onOpenChange={setShowAddExpense}>
                  <DialogTrigger asChild>
                    <Button
                      data-testid="add-expense-button"
                      className="bg-cyan-500 hover:bg-cyan-400 text-black font-bold"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Ajouter une dépense
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#0A0A10] border-cyan-500/30 text-white max-w-2xl">
                    <DialogHeader>
                      <DialogTitle className="text-2xl font-['Orbitron'] text-cyan-400">
                        💰 Quelle dépense veux-tu enregistrer ?
                      </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4 mt-4">
                      {/* Category */}
                      <div>
                        <Label className="text-slate-300 mb-2 block">Type de dépense</Label>
                        <Select
                          value={expenseForm.category}
                          onValueChange={(value) => {
                            setExpenseForm({ ...expenseForm, category: value, subcategory: '' });
                          }}
                        >
                          <SelectTrigger className="bg-black/50 border-slate-700 text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0A0A10] border-slate-700">
                            <SelectItem value="travaux">🔨 Travaux</SelectItem>
                            <SelectItem value="amenagement">🛋️ Aménagement</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Subcategory */}
                      <div>
                        <Label className="text-slate-300 mb-2 block">
                          {expenseForm.category === 'travaux' ? 'Quel type de travaux ?' : 'Qu\'as-tu acheté ?'}
                        </Label>
                        <Select
                          value={expenseForm.subcategory}
                          onValueChange={(value) => setExpenseForm({ ...expenseForm, subcategory: value })}
                        >
                          <SelectTrigger className="bg-black/50 border-slate-700 text-white">
                            <SelectValue placeholder="Choisis une catégorie" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0A0A10] border-slate-700">
                            {categories[expenseForm.category]?.map((cat) => (
                              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Amount */}
                      <div>
                        <Label className="text-slate-300 mb-2 block">💵 Combien as-tu dépensé ?</Label>
                        <Input
                          type="number"
                          value={expenseForm.amount}
                          onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                          placeholder="1500"
                          className="bg-black/50 border-slate-700 text-white"
                        />
                      </div>

                      {/* Description */}
                      <div>
                        <Label className="text-slate-300 mb-2 block">📝 Décris un peu (optionnel)</Label>
                        <Textarea
                          value={expenseForm.description}
                          onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                          placeholder="Ex: Rénovation salle de bain complète"
                          className="bg-black/50 border-slate-700 text-white"
                        />
                      </div>

                      {/* Date */}
                      <div>
                        <Label className="text-slate-300 mb-2 block">📅 Quand ?</Label>
                        <Input
                          type="date"
                          value={expenseForm.date}
                          onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })}
                          className="bg-black/50 border-slate-700 text-white"
                        />
                      </div>

                      <Button
                        onClick={handleAddExpense}
                        className="w-full bg-cyan-500 hover:bg-cyan-400 text-black font-bold"
                      >
                        ✅ Enregistrer
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Expenses List & Chart */}
              <div className="grid md:grid-cols-2 gap-8">
                {/* Chart */}
                {chartData.length > 0 && (
                  <div className="glass-panel p-6 rounded-lg">
                    <h3 className="text-xl font-bold font-['Rajdhani'] text-white mb-4">
                      Répartition par catégorie
                    </h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={chartData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          label={(entry) => `${entry.name}: ${entry.value.toLocaleString()}€`}
                        >
                          {chartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <ChartTooltip
                          contentStyle={{
                            backgroundColor: 'rgba(0, 0, 0, 0.9)',
                            border: '1px solid rgba(0, 240, 255, 0.3)',
                            borderRadius: '8px'
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* List */}
                <div className="glass-panel p-6 rounded-lg">
                  <h3 className="text-xl font-bold font-['Rajdhani'] text-white mb-4">
                    Historique des dépenses
                  </h3>
                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {property.expenses && property.expenses.length > 0 ? (
                      property.expenses.map((expense) => (
                        <div
                          key={expense.expense_id}
                          className="p-4 bg-white/5 border border-white/10 rounded-lg hover:border-cyan-500/30 transition-colors"
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <div className="font-semibold text-white">{expense.subcategory}</div>
                              <div className="text-sm text-slate-400">{expense.description}</div>
                            </div>
                            <div className={`text-lg font-bold ${
                              expense.category === 'travaux' ? 'text-cyan-400' : 'text-purple-400'
                            }`}>
                              {expense.amount.toLocaleString()} €
                            </div>
                          </div>
                          <div className="text-xs text-slate-500 flex items-center gap-2">
                            <Calendar className="h-3 w-3" />
                            {new Date(expense.date).toLocaleDateString('fr-FR')}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center text-slate-400 py-8">
                        <p>Aucune dépense enregistrée</p>
                        <p className="text-sm mt-2">Clique sur "Ajouter une dépense" pour commencer !</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Simulations Tab */}
            <TabsContent value="simulations" className="mt-6">
              <div className="glass-panel p-8 rounded-lg text-center">
                <h3 className="text-2xl font-bold text-white mb-4">
                  Simulations liées à ce bien
                </h3>
                {property.simulations && property.simulations.length > 0 ? (
                  <div className="grid gap-4">
                    {property.simulations.map((sim) => (
                      <div
                        key={sim.simulation_id}
                        className="p-4 bg-white/5 border border-cyan-500/20 rounded-lg cursor-pointer hover:border-cyan-500/50 transition-colors"
                        onClick={() => navigate(`/simulation/${sim.simulation_id}`)}
                      >
                        <div className="flex justify-between items-center">
                          <div className="text-left">
                            <div className="text-white font-semibold">{sim.location}</div>
                            <div className="text-sm text-slate-400">
                              Prêt {sim.loan_type}% sur {sim.loan_duration} ans
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm text-slate-400">Cash-flow trad.</div>
                            <div className={`text-lg font-bold ${
                              sim.cashflow_traditional > 0 ? 'text-green-400' : 'text-red-400'
                            }`}>
                              {sim.cashflow_traditional.toLocaleString()} €
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-slate-400 py-8">
                    <p className="mb-4">Aucune simulation pour ce bien</p>
                    <Button
                      onClick={() => navigate('/simulation/new')}
                      className="bg-cyan-500 hover:bg-cyan-400 text-black font-bold"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Créer une simulation
                    </Button>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </main>
      </div>
    </div>
  );
}
